import React from 'react';
import { TextInput, UnstyledButton } from '@mantine/core';
import { APPROVAL_STATUS_LABELS } from '../monitoring-page.constants';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from './approval-requests-filters.module.scss';

type Props = Pick<
  UseMonitoringPage,
  | 'approvalRequestSearch'
  | 'setApprovalRequestSearch'
  | 'approvalRequestSearchFields'
  | 'setApprovalRequestSearchFields'
  | 'approvalRequestFilterOptions'
  | 'approvalRequestSessionFilter'
  | 'setApprovalRequestSessionFilter'
  | 'approvalRequestStatusFilter'
  | 'setApprovalRequestStatusFilter'
  | 'approvalRequestDateFrom'
  | 'setApprovalRequestDateFrom'
  | 'approvalRequestDateTo'
  | 'setApprovalRequestDateTo'
  | 'clearApprovalRequestFilters'
>;

const SEARCH_FIELDS = [
  { key: 'session', label: 'הפעלה' },
  { key: 'order', label: 'מספר הזמנה' },
  { key: 'actionType', label: 'סוג פעולה' },
  { key: 'reason', label: 'סיבה' },
  { key: 'requestedByName', label: 'נשלח ע״י' },
  { key: 'decidedByName', label: 'אושר/נדחה ע״י' },
] as const;

const STATUS_OPTIONS = ['pending', 'approved', 'denied'] as const;

export const ApprovalRequestsFilters: React.FC<Props> = ({
  approvalRequestSearch,
  setApprovalRequestSearch,
  approvalRequestSearchFields,
  setApprovalRequestSearchFields,
  approvalRequestFilterOptions,
  approvalRequestSessionFilter,
  setApprovalRequestSessionFilter,
  approvalRequestStatusFilter,
  setApprovalRequestStatusFilter,
  approvalRequestDateFrom,
  setApprovalRequestDateFrom,
  approvalRequestDateTo,
  setApprovalRequestDateTo,
  clearApprovalRequestFilters,
}) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  const toggleSearchField = (field: Props['approvalRequestSearchFields'][number]) => {
    setApprovalRequestSearchFields((current) => (
      current.includes(field) ? current.filter((item) => item !== field) : [...current, field]
    ));
  };

  const toggleListValue = (
    current: string[],
    setCurrent: React.Dispatch<React.SetStateAction<string[]>>,
    value: string,
  ) => {
    setCurrent(current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  };

  const activeChips = [
    ...approvalRequestSearchFields.map((field) => ({
      key: `field-${field}`,
      label: SEARCH_FIELDS.find((item) => item.key === field)?.label ?? field,
      onRemove: () => toggleSearchField(field),
    })),
    ...approvalRequestSessionFilter.map((value) => ({
      key: `session-${value}`,
      label: `הפעלה ${value}`,
      onRemove: () => toggleListValue(approvalRequestSessionFilter, setApprovalRequestSessionFilter, value),
    })),
    ...approvalRequestStatusFilter.map((value) => ({
      key: `status-${value}`,
      label: APPROVAL_STATUS_LABELS[value as keyof typeof APPROVAL_STATUS_LABELS] ?? value,
      onRemove: () => toggleListValue(approvalRequestStatusFilter, setApprovalRequestStatusFilter, value),
    })),
    ...(approvalRequestDateFrom ? [{
      key: 'date-from',
      label: `מ-${approvalRequestDateFrom}`,
      onRemove: () => setApprovalRequestDateFrom(''),
    }] : []),
    ...(approvalRequestDateTo ? [{
      key: 'date-to',
      label: `עד ${approvalRequestDateTo}`,
      onRemove: () => setApprovalRequestDateTo(''),
    }] : []),
  ];

  const hasActive = activeChips.length > 0;

  return (
    <div className={classes.root}>
      <div className={classes.searchBar}>
        {activeChips.map((chip) => (
          <span key={chip.key} className={classes.chip}>
            {chip.label}
            <UnstyledButton
              className={classes.chipRemove}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                chip.onRemove();
              }}
              data-testid={`monitoring-approval-chip-remove-${chip.key}`}
            >
              {'×'}
            </UnstyledButton>
          </span>
        ))}
        <TextInput
          variant="unstyled"
          classNames={{ root: classes.inputRoot, input: classes.input }}
          placeholder={!hasActive ? 'חיפוש...' : ''}
          value={approvalRequestSearch}
          onChange={(event) => setApprovalRequestSearch(event.currentTarget.value)}
          data-testid="monitoring-approval-search"
        />
        {hasActive && (
          <UnstyledButton className={classes.clearBtn} type="button" onClick={clearApprovalRequestFilters} aria-label="נקה סינונים" data-testid="monitoring-approval-clear">
            {'×'}
          </UnstyledButton>
        )}
        <UnstyledButton
          className={`${classes.filterBtn} ${hasActive ? classes.filterBtnActive : ''}`}
          type="button"
          onClick={() => setDropdownOpen((current) => !current)}
          aria-label="סינון"
          data-testid="monitoring-approval-filter-toggle"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M1 2.5A.5.5 0 0 1 1.5 2h13a.5.5 0 0 1 .354.854L10 8.207V13.5a.5.5 0 0 1-.276.447l-3 1.5A.5.5 0 0 1 6 15V8.207L1.146 2.854A.5.5 0 0 1 1 2.5z" />
          </svg>
        </UnstyledButton>
      </div>

      {dropdownOpen && (
        <>
          <div className={classes.dropdown}>
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'שדות חיפוש'}</div>
              {SEARCH_FIELDS.map((field) => (
                <UnstyledButton
                  key={field.key}
                  type="button"
                  className={approvalRequestSearchFields.includes(field.key) ? `${classes.dropdownItem} ${classes.active}` : classes.dropdownItem}
                  onClick={() => toggleSearchField(field.key)}
                  data-testid={`monitoring-approval-field-${field.key}`}
                >
                  <span className={classes.checkmark}>{approvalRequestSearchFields.includes(field.key) ? '✓' : ''}</span>
                  {field.label}
                </UnstyledButton>
              ))}
            </div>

            {approvalRequestFilterOptions.sessions.length > 0 && (
              <>
                <div className={classes.dropdownDivider} />
                <div className={classes.dropdownSection}>
                  <div className={classes.dropdownSectionTitle}>{'הפעלה'}</div>
                  {approvalRequestFilterOptions.sessions.map((sessionId) => {
                    const value = String(sessionId);
                    return (
                      <UnstyledButton
                        key={value}
                        type="button"
                        className={approvalRequestSessionFilter.includes(value) ? `${classes.dropdownItem} ${classes.active}` : classes.dropdownItem}
                        onClick={() => toggleListValue(approvalRequestSessionFilter, setApprovalRequestSessionFilter, value)}
                        data-testid={`monitoring-approval-session-${value}`}
                      >
                        <span className={classes.checkmark}>{approvalRequestSessionFilter.includes(value) ? '✓' : ''}</span>
                        {value}
                      </UnstyledButton>
                    );
                  })}
                </div>
              </>
            )}

            <div className={classes.dropdownDivider} />
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'תאריך'}</div>
              <label className={classes.dateRow}>
                <span className={classes.dateLabel}>{'מ־'}</span>
                <TextInput
                  type="date"
                  variant="unstyled"
                  classNames={{ root: classes.dateInputRoot, input: classes.dateInput }}
                  value={approvalRequestDateFrom}
                  onChange={(event) => setApprovalRequestDateFrom(event.currentTarget.value)}
                  data-testid="monitoring-approval-date-from"
                />
              </label>
              <label className={classes.dateRow}>
                <span className={classes.dateLabel}>{'עד'}</span>
                <TextInput
                  type="date"
                  variant="unstyled"
                  classNames={{ root: classes.dateInputRoot, input: classes.dateInput }}
                  value={approvalRequestDateTo}
                  onChange={(event) => setApprovalRequestDateTo(event.currentTarget.value)}
                  data-testid="monitoring-approval-date-to"
                />
              </label>
            </div>

            <div className={classes.dropdownDivider} />
            <div className={classes.dropdownSection}>
              <div className={classes.dropdownSectionTitle}>{'סטטוס'}</div>
              {STATUS_OPTIONS.map((value) => (
                <UnstyledButton
                  key={value}
                  type="button"
                  className={approvalRequestStatusFilter.includes(value) ? `${classes.dropdownItem} ${classes.active}` : classes.dropdownItem}
                  onClick={() => toggleListValue(approvalRequestStatusFilter, setApprovalRequestStatusFilter, value)}
                  data-testid={`monitoring-approval-status-${value}`}
                >
                  <span className={classes.checkmark}>{approvalRequestStatusFilter.includes(value) ? '✓' : ''}</span>
                  {APPROVAL_STATUS_LABELS[value]}
                </UnstyledButton>
              ))}
            </div>
          </div>
          <UnstyledButton className={classes.backdrop} type="button" aria-label="סגור סינון" onClick={() => setDropdownOpen(false)} />
        </>
      )}
    </div>
  );
};
